import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["truncationWidth", "text", "onTooltipShown"];
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
/** @jsx jsx */

import { memo, useRef } from 'react';
import { css, jsx } from '@emotion/react';
import { lazyForPaint, LazySuspense } from 'react-loosely-lazy';
import { getBooleanFF } from '@atlaskit/platform-feature-flags';
// eslint-disable-next-line @atlaskit/design-system/no-deprecated-imports
import { fontSize, gridSize } from '@atlaskit/theme/constants';
import Tooltip from '@atlaskit/tooltip';
import Step from './internal/step';
import useOverflowable from './internal/use-overflowable';
var gridSizeUnit = gridSize();
var height = gridSize() * 3 / fontSize();
var AKTooltip = lazyForPaint(function () {
  return import( /* webpackChunkName: "@atlaskit-internal_Tooltip" */'@atlaskit/tooltip');
}, {
  ssr: false
});
var itemWrapperStyles = css({
  display: 'flex',
  boxSizing: 'border-box',
  maxWidth: '100%',
  height: "".concat(height, "em"),
  margin: "var(--ds-space-0, 0px)",
  padding: "var(--ds-space-0, 0px)",
  flexDirection: 'row',
  lineHeight: "".concat(height, "em"),
  '&:not(:last-child)::after': {
    width: gridSizeUnit,
    padding: "var(--ds-space-0, 0px)".concat(" ", "var(--ds-space-100, 8px)"),
    flexShrink: 0,
    content: '"/"',
    textAlign: 'center'
  }
});
var VAR_STEP_TRUNCATION_WIDTH = '--max-width';
var staticItemWithTruncationStyles = css({
  maxWidth: "var(".concat(VAR_STEP_TRUNCATION_WIDTH, ")"),
  fontWeight: "var(--ds-font-weight-regular, 400)"
});
var staticItemWithoutTruncationStyles = css({
  minWidth: 0,
  flexShrink: 1,
  fontWeight: "var(--ds-font-weight-regular, 400)"
});
var BreadcrumbsItem = /*#__PURE__*/memo(function (props) {
  var buttonRef = useRef(null);
  var truncationWidth = props.truncationWidth,
    text = props.text,
    onTooltipShown = props.onTooltipShown,
    restButtonProps = _objectWithoutProperties(props, _excluded);
  var showTooltip = useOverflowable(truncationWidth, buttonRef);
  var buttonProps = _objectSpread(_objectSpread({}, restButtonProps), {}, {
    ref: buttonRef,
    hasOverflow: showTooltip
  });

  // Note: cast to `any` is required to type verification - see https://github.com/frenic/csstype#what-should-i-do-when-i-get-type-errors
  var dynamicItemStyles = _defineProperty({}, VAR_STEP_TRUNCATION_WIDTH, "".concat(truncationWidth, "px"));
  var step = jsx(Step, _extends({}, buttonProps, {
    css: truncationWidth ? staticItemWithTruncationStyles : staticItemWithoutTruncationStyles,
    style: dynamicItemStyles
  }), text);
  if (getBooleanFF('platform.design-system-team.remove-lazy-loading-of-tooltip-in-breadcrumbs_pki8p')) {
    return jsx("li", {
      css: itemWrapperStyles
    }, showTooltip ? jsx(Tooltip, {
      content: text,
      position: "bottom",
      onShow: onTooltipShown
    }, step) : step);
  }
  return jsx("li", {
    css: itemWrapperStyles
  }, showTooltip ? /* The div exists because of tooltip */
  jsx(LazySuspense, {
    fallback: jsx("div", null, step)
  }, jsx(AKTooltip, {
    content: text,
    position: "bottom",
    onShow: onTooltipShown
  }, step)) : step);
});

// eslint-disable-next-line @repo/internal/react/require-jsdoc
export default BreadcrumbsItem;